/**
 * Primary navigation — WCAG 2.1 AAA compliant.
 *
 * Desktop: drawer uses display:contents (CSS), no JS state needed.
 * Mobile:  JS manages .is-open class, aria-expanded, aria-hidden, and inert
 *          so assistive technology cannot reach closed menu items.
 *
 * Features: Escape to close, outside-click to close, focus → first item on
 * open, body scroll-lock while open, breakpoint listener to reset state.
 */

const MOBILE_MQ = window.matchMedia('(max-width: 767.98px)');

export function initNavigation() {
  const toggle = document.querySelector('.flip-nav__toggle');
  const drawer = document.getElementById('flip-nav-drawer');
  const overlay = document.getElementById('flip-nav-overlay');

  if (!toggle || !drawer) return;

  // ── State helpers ──────────────────────────────────────────────────────────

  function setOpen(open) {
    const isMobile = MOBILE_MQ.matches;

    drawer.classList.toggle('is-open', open);
    overlay?.classList.toggle('is-open', isMobile && open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      open
        ? toggle.dataset.labelClose || 'Close navigation menu'
        : toggle.dataset.labelOpen  || 'Open navigation menu'
    );

    if (isMobile) {
      // inert prevents keyboard/AT access when panel is visually hidden
      drawer.inert = !open;
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');

      // Prevent background scroll while menu is open
      document.body.classList.toggle('nav-open', open);
    }

    if (open) {
      // Move focus to first interactive element inside the drawer
      const firstFocusable = drawer.querySelector('a, button');
      requestAnimationFrame(() => firstFocusable?.focus());
    }
  }

  function resetToDesktop() {
    drawer.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    drawer.inert = false;
    drawer.removeAttribute('aria-hidden');
    document.body.classList.remove('nav-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', toggle.dataset.labelOpen || 'Open navigation menu');
  }

  // ── Initialise ────────────────────────────────────────────────────────────

  if (MOBILE_MQ.matches) {
    // Start closed on mobile
    setOpen(false);
  } else {
    // Desktop: ensure clean state
    resetToDesktop();
  }

  // ── Event listeners ───────────────────────────────────────────────────────

  toggle.addEventListener('click', () => {
    setOpen(!drawer.classList.contains('is-open'));
  });

  // Escape key closes the menu and returns focus to the toggle
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      setOpen(false);
      toggle.focus();
    }
  });

  // Outside-click closes menu
  document.addEventListener('click', (e) => {
    if (
      MOBILE_MQ.matches &&
      drawer.classList.contains('is-open') &&
      !drawer.contains(e.target) &&
      !toggle.contains(e.target)
    ) {
      setOpen(false);
    }
  });

  // Breakpoint change: reset when switching to desktop, close when going mobile
  MOBILE_MQ.addEventListener('change', (e) => {
    if (e.matches) {
      // Just switched to mobile — start closed
      setOpen(false);
    } else {
      // Just switched to desktop — remove all mobile state
      resetToDesktop();
    }
  });
}
